// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * HidePetButton — "Hide from my list" action (vic/vetpo#10). Rendered in
 * PetDatagrid rows and, since vic/vetpo#13, in PetShow's header — the page
 * every vet reaches from the dashboard's View button (PetList has no sidebar
 * entry, so the row placement alone was unreachable).
 *
 * A clinic had no self-serve way to remove an inactive patient from its own
 * list; support was hand-writing SQL against prod. The backend
 * (vic/ada-core#139) added a per-clinic hide, `POST /api/pets/:id/clinic-hide`,
 * that subtracts a pet from THIS clinic's `/api/pets` reads only — the owner,
 * other clinics, and `pets.status` are untouched. Role EXACTLY 'vet' (not
 * 'vetadmin' — see ada-core clinicHide.ts), master or sub-account: a
 * sub-account's hide is recorded against its master and applies clinic-wide,
 * so no extra masterAccount check is needed here (unlike AssignVetButton,
 * which IS master-only).
 *
 * Same-session reversibility ships here: hiding raises an Undo notice that
 * calls `clinic-unhide` for the pet just hidden. The notice goes through the
 * app-level react-admin notify(), NOT a Snackbar in this component's state:
 * a successful hide refreshes the list, the list drops the pet, and the row
 * (or, on PetShow, the whole page via onHidden) unmounts this button — a
 * local Snackbar died with it, so Undo never survived in practice (#13).
 * Persistent "Show hidden" / Unhide browsing — for a patient
 * hidden in an earlier session — is `HiddenPetsPanel`, wired into `PetList`
 * behind a vet-only toggle (vic/vetpo#12) and in the dashboard's "Hidden
 * patients" dialog (#13), reading the vet-reachable
 * `GET /api/pets/clinic-hidden` (vic/ada-core#150).
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SnackbarContent,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import {
  useCloseNotification,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import type { Pet } from '../types'
import { authHeaders } from '../utils/api'

/**
 * The Undo notice's content. It is rendered by the Layout's <Notification>,
 * so its hooks resolve there and keep working after HidePetButton unmounts.
 */
const HiddenUndoNotice = React.forwardRef<HTMLDivElement, { petId: number; label: string }>(
  ({ petId, label }, ref) => {
    const translate = useTranslate()
    const notify = useNotify()
    const refresh = useRefresh()
    const close = useCloseNotification()

    const handleUndo = async () => {
      close()
      try {
        const response = await deadlineFetch(`/api/pets/${petId}/clinic-unhide`, {
          method: 'POST',
          headers: authHeaders(),
        })
        const data = await response.json().catch(() => ({}))
        if (response.status === 200 && data?.success) {
          notify(translate('resources.pets.hide.undo_success'), { type: 'success' })
          refresh()
        } else {
          notify(data?.errors?.[0]?.message || translate('resources.pets.hide.error'), {
            type: 'error',
          })
        }
      } catch (_e) {
        notify(translate('resources.pets.hide.error'), { type: 'error' })
      }
    }

    return (
      <SnackbarContent
        ref={ref}
        message={translate('resources.pets.hide.success', { pet: label })}
        action={
          <Button color="inherit" size="small" onClick={handleUndo}>
            {translate('resources.pets.hide.undo')}
          </Button>
        }
      />
    )
  },
)
HiddenUndoNotice.displayName = 'HiddenUndoNotice'

interface HidePetButtonProps {
  /** Called after a successful hide — PetShow uses it to leave the page. */
  onHidden?: () => void
}

const HidePetButton: React.FC<HidePetButtonProps> = ({ onHidden }) => {
  const translate = useTranslate()
  const record = useRecordContext<Pet>()
  const { identity } = useGetIdentity()
  const notify = useNotify()
  const refresh = useRefresh()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Only a 'vet' can hide/unhide — the backend 403s everyone else, including
  // 'vetadmin'.
  if (!record || identity?.role !== 'vet') return null

  const petLabel = record.name || `#${record.id}`

  const handleHide = async () => {
    setSubmitting(true)
    try {
      const response = await deadlineFetch(`/api/pets/${record.id}/clinic-hide`, {
        method: 'POST',
        headers: authHeaders(),
      })
      const data = await response.json().catch(() => ({}))
      if (response.status === 200 && data?.success) {
        setConfirmOpen(false)
        notify(<HiddenUndoNotice petId={Number(record.id)} label={petLabel} />, {
          autoHideDuration: 8000,
        })
        refresh()
        onHidden?.()
      } else {
        notify(data?.errors?.[0]?.message || translate('resources.pets.hide.error'), {
          type: 'error',
        })
      }
    } catch (_e) {
      notify(translate('resources.pets.hide.error'), { type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Button
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          setConfirmOpen(true)
        }}
      >
        {translate('resources.pets.hide.action')}
      </Button>
      {confirmOpen && (
        <Dialog
          open
          onClose={() => setConfirmOpen(false)}
          // Same fix as AssignVetModal: this dialog is a REACT child of the
          // Datagrid row it renders from, and MUI's portal doesn't stop React
          // from bubbling synthetic events up that tree — without this a click
          // inside the dialog also fires the row's rowClick.
          onClick={(e) => e.stopPropagation()}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{translate('resources.pets.hide.confirm_title')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {translate('resources.pets.hide.confirm_body', { pet: petLabel })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
              {translate('ra.action.cancel')}
            </Button>
            {/* Primary, not warning-orange: hiding is reversible (Undo notice)
                and not clinical, and orange is reserved for alerts. */}
            <Button onClick={handleHide} variant="contained" disabled={submitting}>
              {translate('resources.pets.hide.action')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default HidePetButton
